<main class="requisitions-page">
  <header class="page-header" [class.detail]="isDetailRoute">
    @if (isDetailRoute) {
      <a class="back-link" routerLink="/requisitions">
        <mat-icon aria-hidden="true">arrow_back</mat-icon>
        {{ t.getRequisitionBackText() }}
      </a>
    }
    <div class="heading-row">
      <div>
        <h1>{{ pageTitle }}</h1>
        @if (request) {
          <p class="reference">{{ request.reference }}</p>
        }
      </div>
      @if (request) {
        <span class="status-chip" [attr.data-status]="request.status">
          <mat-icon aria-hidden="true">{{
            statusIcon(request.status)
          }}</mat-icon>
          {{ t.getRequisitionStatusText(request.status) }}
        </span>
      } @else if (!isDetailRoute) {
        <a mat-stroked-button class="browse-button" routerLink="/marketplace">
          <mat-icon aria-hidden="true">storefront</mat-icon>
          {{ t.getRequisitionCartBrowseText() }}
        </a>
      }
    </div>
    @if (request && request.submittedAt) {
      <p class="submitted-at">
        {{ t.getRequisitionSubmittedAtText() }}:
        {{ request.submittedAt | date: 'medium' }}
      </p>
    }
  </header>

  @if (request?.reviewedAt) {
    <p class="submitted-at">
      {{ t.getRequisitionReviewedAtText() }}:
      {{ request?.reviewedAt | date: 'medium' }}
    </p>
  }

  @if (
    request &&
    (request.status === requisitionStatus.APPROVED ||
      request.status === requisitionStatus.PARTIALLY_APPROVED)
  ) {
    <section class="card approved-actions">
      <div class="actions">
        <button
          mat-stroked-button
          type="button"
          [disabled]="downloadingJSON"
          [attr.aria-busy]="downloadingJSON"
          (click)="downloadRequisitionJSON()"
        >
          <mat-icon aria-hidden="true">download</mat-icon>
          {{ t.getRequisitionJSONDownloadText() }}
        </button>
        <button
          mat-stroked-button
          type="button"
          [disabled]="busy"
          (click)="openPurchaseOrder()"
        >
          {{
            purchaseOrderFailed
              ? t.getPurchaseOrderRetryText()
              : t.getPurchaseOrderCreateText()
          }}
        </button>
      </div>
      @if (purchaseOrderFailed && error) {
        <p role="alert" class="error">{{ error }}</p>
      }
    </section>
  }

  @if (loading) {
    <div class="state">
      <mat-spinner diameter="32"></mat-spinner
      >{{ t.getRequisitionLoadingText() }}
    </div>
  } @else if (!request) {
    @if (error) {
      <p role="alert" class="error">{{ error }}</p>
    }
    @if (!requests.length && !error) {
      <div class="empty-state">
        <span class="empty-icon"
          ><mat-icon aria-hidden="true">assignment</mat-icon></span
        >
        <p>{{ t.getRequisitionEmptyText() }}</p>
      </div>
    }
    @if (requests.length) {
      <div class="list-filters">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ t.getRequisitionStatusFilterText() }}</mat-label>
          <mat-icon matPrefix aria-hidden="true">filter_list</mat-icon>
          <mat-select
            [(ngModel)]="statusFilter"
            (selectionChange)="filterChanged()"
          >
            <mat-option value="all">{{
              t.getRequisitionAllStatusesText()
            }}</mat-option>
            <mat-option value="awaiting_your_review">{{
              t.getRequisitionAwaitingYourReviewText()
            }}</mat-option>
            @for (status of statusFilters; track status) {
              <mat-option [value]="status">{{
                t.getRequisitionStatusText(status)
              }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (statusFilter !== 'all') {
          <button mat-button (click)="clearFilter()">
            <mat-icon aria-hidden="true">close</mat-icon>
            {{ t.getRequisitionClearFilterText() }}
          </button>
        }
      </div>
      @if (!filteredRequests.length) {
        <p class="filter-empty" role="status">
          {{ t.getRequisitionNoFilterMatchesText() }}
        </p>
      }
    }
    <section class="saved-list" [attr.aria-label]="t.getRequisitionSavedText()">
      @for (entry of pagedRequests; track entry.id) {
        <a class="saved-card" [routerLink]="['/requisitions', entry.id]">
          <span class="saved-card-icon">
            <mat-icon aria-hidden="true">assignment</mat-icon>
          </span>
          <span class="saved-card-content">
            <span class="saved-card-top">
              <span class="saved-reference">{{ entry.reference }}</span>
              <span class="status-chip" [attr.data-status]="entry.status">
                <mat-icon aria-hidden="true">{{
                  statusIcon(entry.status)
                }}</mat-icon>
                {{
                  canReview(entry)
                    ? t.getRequisitionAwaitingYourReviewText()
                    : t.getRequisitionStatusText(entry.status)
                }}
              </span>
            </span>
            <strong class="saved-recipient">{{
              entry.recipient || t.getRequisitionRecipientPendingText()
            }}</strong>
            <span class="saved-card-meta">
              <span>{{
                t.getRequisitionItemCountText(entry.items.length)
              }}</span>
              @if (entry.submittedAt) {
                <span class="meta-divider" aria-hidden="true"></span>
                <span
                  >{{ t.getRequisitionSubmittedAtText() }}:
                  {{ entry.submittedAt | date: 'mediumDate' }}</span
                >
              }
            </span>
          </span>
          <mat-icon class="saved-card-arrow" aria-hidden="true"
            >chevron_right</mat-icon
          >
        </a>
      }
    </section>
    @if (filteredRequests.length) {
      <mat-paginator
        [length]="filteredRequests.length"
        [pageIndex]="pageIndex"
        [pageSize]="pageSize"
        [pageSizeOptions]="[10, 25, 50]"
        (page)="onPageChange($event)"
      ></mat-paginator>
    }
  } @else {
    @if (request.status !== requisitionStatus.DRAFT) {
      @if (error && !canReview()) {
        <p role="alert" class="error">{{ error }}</p>
      }
      @if (notice) {
        <p role="status" class="notice">{{ notice }}</p>
      }
      @if (canReview()) {
        <section class="card">
          <h2>{{ t.getRequisitionBuyerReviewText() }}</h2>
          <p>{{ t.getRequisitionReviewHintText() }}</p>
        </section>
      }
    }
    <section class="card">
      <h2>{{ t.getRequisitionDetailsText() }}</h2>
      @if (request.status === requisitionStatus.DRAFT) {
        <p class="section-intro">{{ t.getRequisitionRequiredHintText() }}</p>
        <div class="fields">
          <mat-form-field appearance="outline" subscriptSizing="dynamic"
            ><mat-label>{{ t.getRequisitionRecipientText() }}</mat-label
            ><input
              matInput
              required
              [(ngModel)]="request.recipient"
              [disabled]="draftFieldsDisabled()"
          /></mat-form-field>
          <div
            class="location-group wide"
            role="group"
            [attr.aria-label]="t.getRequisitionLocationText()"
          >
            <p class="group-title">{{ t.getRequisitionLocationText() }}</p>
            <p class="field-hint">{{ t.getRequisitionLocationHintText() }}</p>
            <div class="fields">
              <mat-form-field appearance="outline" subscriptSizing="dynamic"
                ><mat-label>{{ t.getRequisitionDeliveryText() }}</mat-label
                ><input
                  matInput
                  [(ngModel)]="request.deliveryAddress"
                  [disabled]="draftFieldsDisabled()"
              /></mat-form-field>
              <mat-form-field appearance="outline" subscriptSizing="dynamic"
                ><mat-label>{{ t.getRequisitionCollectionText() }}</mat-label
                ><input
                  matInput
                  [(ngModel)]="request.collectionLocation"
                  [disabled]="draftFieldsDisabled()"
              /></mat-form-field>
            </div>
          </div>
          <mat-form-field appearance="outline" subscriptSizing="dynamic"
            ><mat-label>{{ t.getRequisitionNeededByText() }}</mat-label
            ><input
              matInput
              type="date"
              [(ngModel)]="request.neededBy"
              [disabled]="draftFieldsDisabled()"
          /></mat-form-field>
          <mat-form-field
            appearance="outline"
            subscriptSizing="dynamic"
            class="wide"
            ><mat-label>{{ t.getRequisitionNoteText() }}</mat-label
            ><textarea
              matInput
              [(ngModel)]="request.note"
              [disabled]="draftFieldsDisabled()"
            ></textarea>
          </mat-form-field>
        </div>
        <details class="additional-details">
          <summary>
            {{ t.getRequisitionAdditionalRequestText() }}
            @if (
              request.businessUnitId ||
              request.storageLocation ||
              request.companyCode
            ) {
              <span class="details-filled">{{
                t.getRequisitionDetailsFilledText()
              }}</span>
            }
          </summary>
          <div class="fields">
            <mat-form-field appearance="outline" subscriptSizing="dynamic"
              ><mat-label>{{ t.getRequisitionBusinessUnitText() }}</mat-label
              ><input
                matInput
                [(ngModel)]="request.businessUnitId"
                [disabled]="draftFieldsDisabled()"
            /></mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic"
              ><mat-label>{{ t.getRequisitionStorageText() }}</mat-label
              ><input
                matInput
                [(ngModel)]="request.storageLocation"
                [disabled]="draftFieldsDisabled()"
            /></mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic"
              ><mat-label>{{ t.getRequisitionCompanyCodeText() }}</mat-label
              ><input
                matInput
                [(ngModel)]="request.companyCode"
                [disabled]="draftFieldsDisabled()"
            /></mat-form-field>
          </div>
        </details>
      } @else {
        <dl class="request-summary">
          <div>
            <dt>{{ t.getRequisitionRecipientText() }}</dt>
            <dd>{{ request.recipient }}</dd>
          </div>
          @if (request.deliveryAddress) {
            <div>
              <dt>{{ t.getRequisitionDeliveryText() }}</dt>
              <dd>{{ request.deliveryAddress }}</dd>
            </div>
          }
          @if (request.collectionLocation) {
            <div>
              <dt>{{ t.getRequisitionCollectionText() }}</dt>
              <dd>{{ request.collectionLocation }}</dd>
            </div>
          }
          @if (request.neededBy) {
            <div>
              <dt>{{ t.getRequisitionNeededByText() }}</dt>
              <dd>{{ request.neededBy }}</dd>
            </div>
          }
          @if (request.note) {
            <div class="summary-wide">
              <dt>{{ t.getRequisitionNoteText() }}</dt>
              <dd>{{ request.note }}</dd>
            </div>
          }
          @if (request.businessUnitId) {
            <div>
              <dt>{{ t.getRequisitionBusinessUnitText() }}</dt>
              <dd>{{ request.businessUnitId }}</dd>
            </div>
          }
          @if (request.storageLocation) {
            <div>
              <dt>{{ t.getRequisitionStorageText() }}</dt>
              <dd>{{ request.storageLocation }}</dd>
            </div>
          }
          @if (request.companyCode) {
            <div>
              <dt>{{ t.getRequisitionCompanyCodeText() }}</dt>
              <dd>{{ request.companyCode }}</dd>
            </div>
          }
        </dl>
      }
    </section>

    <section class="card">
      <h2>{{ t.getRequisitionItemsText() }}</h2>
      @if (!request.items.length) {
        <p class="error">{{ t.getRequisitionEmptyItemsText() }}</p>
      }
      @for (item of request.items; track item.productId) {
        <article class="item">
          <div class="item-overview">
            <div class="item-photo">
              <app-product-image
                [picture]="itemPhotos[item.productId]"
                [revision]="itemPhotosRevision"
                [name]="item.productName"
                [compact]="true"
                size="card"
              />
            </div>
            <div class="item-heading">
              <div>
                <h3>{{ item.productName }}</h3>
                @if (item.brand) {
                  <p>{{ item.brand }}</p>
                }
                <p>
                  {{ t.getMarketplaceSupplierText() }}: {{ item.supplierName }}
                </p>
              </div>
              @if (request.status === requisitionStatus.DRAFT && !readonly) {
                <button
                  mat-button
                  type="button"
                  [disabled]="busy"
                  (click)="removeItem(item)"
                >
                  <mat-icon aria-hidden="true">delete_outline</mat-icon>
                  {{ t.getRequisitionCartRemoveText() }}
                </button>
              }
            </div>
          </div>
          <div class="fields">
            <mat-form-field appearance="outline" subscriptSizing="dynamic"
              ><mat-label>{{
                request.status === requisitionStatus.DRAFT
                  ? t.getQuantityText()
                  : t.getRequisitionRequestedQuantityText()
              }}</mat-label
              ><input
                #quantityInput
                matInput
                type="number"
                [step]="marketplace.wholeUnit(item.unitOfMeasure) ? 1 : 'any'"
                min="0"
                [(ngModel)]="item.requestedQuantity"
                (ngModelChange)="clearQuantityError()"
                [disabled]="draftFieldsDisabled()"
            /></mat-form-field>
            <p>
              {{ t.getRequisitionCartUnitText() }}:
              {{ t.getMarketplaceRequestUnitValueText(item.unitOfMeasure) }}
            </p>
            <mat-form-field appearance="outline" subscriptSizing="dynamic"
              ><mat-label>{{ t.getRequisitionUnitPriceText() }}</mat-label
              ><input
                matInput
                type="number"
                min="0"
                step="any"
                [(ngModel)]="item.priceAmount"
                [disabled]="draftFieldsDisabled()"
            /></mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic"
              ><mat-label>{{ t.getRequisitionCurrencyText() }}</mat-label
              ><input
                matInput
                [(ngModel)]="item.priceCurrency"
                [disabled]="draftFieldsDisabled()"
            /></mat-form-field>
          </div>
          <details class="additional-details">
            <summary>
              {{ t.getRequisitionAdditionalItemText() }}
              @if (item.itemId || item.supplierId || item.batchId) {
                <span class="details-filled">{{
                  t.getRequisitionDetailsFilledText()
                }}</span>
              }
            </summary>
            <div class="fields">
              <mat-form-field appearance="outline" subscriptSizing="dynamic"
                ><mat-label>{{ t.getRequisitionItemReferenceText() }}</mat-label
                ><input
                  matInput
                  [(ngModel)]="item.itemId"
                  [disabled]="draftFieldsDisabled()"
              /></mat-form-field>
              <mat-form-field appearance="outline" subscriptSizing="dynamic"
                ><mat-label>{{
                  t.getRequisitionSupplierReferenceText()
                }}</mat-label
                ><input
                  matInput
                  [(ngModel)]="item.supplierId"
                  [disabled]="draftFieldsDisabled()"
              /></mat-form-field>
              <mat-form-field appearance="outline" subscriptSizing="dynamic"
                ><mat-label>{{ t.getRequisitionBatchText() }}</mat-label
                ><input
                  matInput
                  [(ngModel)]="item.batchId"
                  [disabled]="draftFieldsDisabled()"
              /></mat-form-field>
            </div>
          </details>
          @if (!validQuantity(item)) {
            <p class="error">
              {{
                marketplace.wholeUnit(item.unitOfMeasure)
                  ? t.getRequisitionCartWholeQuantityText()
                  : t.getRequisitionCartPositiveQuantityText()
              }}
            </p>
          }
          <p>
            {{ t.getRequisitionUnitPriceText() }}:
            {{
              item.priceAmount !== null && item.priceCurrency
                ? formatPrice(item.priceAmount, item.priceCurrency)
                : t.getMarketplacePriceNotProvidedText()
            }}
          </p>
          @if (canReview()) {
            <div class="review-fields fields">
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>{{ t.getRequisitionDecisionText() }}</mat-label>
                <mat-select
                  required
                  [(ngModel)]="reviewDecisions[item.productId].outcome"
                  (ngModelChange)="decisionChanged(item)"
                  [disabled]="busy || reviewBlocked"
                >
                  <mat-option [value]="requisitionStatus.APPROVED">{{
                    t.getRequisitionApproveText()
                  }}</mat-option>
                  <mat-option [value]="requisitionStatus.REJECTED">{{
                    t.getRequisitionRejectText()
                  }}</mat-option>
                </mat-select>
              </mat-form-field>
              @if (
                reviewDecisions[item.productId].outcome ===
                requisitionStatus.APPROVED
              ) {
                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{
                    t.getRequisitionApprovedQuantityText()
                  }}</mat-label>
                  <input
                    matInput
                    required
                    type="number"
                    min="0"
                    [max]="item.requestedQuantity"
                    [step]="
                      marketplace.wholeUnit(item.unitOfMeasure) ? 1 : 'any'
                    "
                    [(ngModel)]="
                      reviewDecisions[item.productId].approvedQuantity
                    "
                    (ngModelChange)="error = ''"
                    [disabled]="busy || reviewBlocked"
                  />
                </mat-form-field>
                <p class="wide field-hint">
                  {{ t.getRequisitionReviewQuantityHintText() }}
                </p>
              } @else if (
                reviewDecisions[item.productId].outcome ===
                requisitionStatus.REJECTED
              ) {
                <mat-form-field
                  appearance="outline"
                  subscriptSizing="dynamic"
                  class="wide"
                >
                  <mat-label>{{
                    t.getRequisitionRejectionReasonText()
                  }}</mat-label>
                  <textarea
                    matInput
                    required
                    maxlength="2048"
                    [(ngModel)]="
                      reviewDecisions[item.productId].rejectionReason
                    "
                    (ngModelChange)="error = ''"
                    [disabled]="busy || reviewBlocked"
                  ></textarea>
                </mat-form-field>
              }
              @if (
                reviewDecisions[item.productId].outcome && !validDecision(item)
              ) {
                <p class="error wide">
                  {{
                    reviewDecisions[item.productId].outcome ===
                    requisitionStatus.REJECTED
                      ? t.getRequisitionRejectionReasonValidationText()
                      : t.getRequisitionReviewValidationText()
                  }}
                </p>
              }
            </div>
          } @else if (item.outcome) {
            <dl
              class="request-summary review-result"
              [class.review-result-approved]="
                item.outcome === requisitionStatus.APPROVED
              "
              [class.review-result-rejected]="
                item.outcome === requisitionStatus.REJECTED
              "
            >
              <div>
                <dt>{{ t.getRequisitionDecisionText() }}</dt>
                <dd class="review-outcome">
                  <mat-icon aria-hidden="true">{{
                    item.outcome === requisitionStatus.REJECTED
                      ? 'cancel'
                      : 'check_circle'
                  }}</mat-icon>
                  {{ t.getRequisitionStatusText(item.outcome) }}
                </dd>
              </div>
              <div>
                <dt>{{ t.getRequisitionRequestedQuantityText() }}</dt>
                <dd>
                  {{ item.requestedQuantity }}
                  {{ t.getMarketplaceRequestUnitValueText(item.unitOfMeasure) }}
                </dd>
              </div>
              <div>
                <dt>{{ t.getRequisitionApprovedQuantityText() }}</dt>
                <dd>
                  {{ item.approvedQuantity }}
                  {{ t.getMarketplaceRequestUnitValueText(item.unitOfMeasure) }}
                </dd>
              </div>
              @if (item.rejectionReason) {
                <div class="summary-wide">
                  <dt>{{ t.getRequisitionRejectionReasonText() }}</dt>
                  <dd>{{ item.rejectionReason }}</dd>
                </div>
              }
            </dl>
          }
        </article>
      }
      <div class="totals">
        @for (total of totals(); track total.currency) {
          <p>
            {{ t.getRequisitionKnownSubtotalText() }}:
            <strong>{{ formatPrice(total.amount, total.currency) }}</strong>
          </p>
        }
      </div>
      @if (unpricedCount()) {
        <p>{{ t.getRequisitionUnpricedText(unpricedCount()) }}</p>
      }
    </section>

    @if (canReview()) {
      <section class="card review-actions">
        <h2>{{ t.getRequisitionBulkActionsText() }}</h2>
        @if (error) {
          <p role="alert" class="error">{{ error }}</p>
        }
        @if (busy) {
          <p role="status">{{ t.getRequisitionReviewSavingText() }}</p>
        }
        @if (reviewBlocked) {
          <button
            mat-stroked-button
            type="button"
            [disabled]="busy"
            (click)="reloadReview()"
          >
            {{ t.getRequisitionReloadReviewText() }}
          </button>
        } @else {
          <div class="actions bulk-actions">
            <button
              mat-stroked-button
              type="button"
              [disabled]="busy || reviewBlocked"
              (click)="approveAll()"
            >
              <mat-icon aria-hidden="true">done_all</mat-icon>
              {{ t.getRequisitionApproveAllText() }}
            </button>
            <button
              mat-stroked-button
              type="button"
              [disabled]="busy || reviewBlocked"
              (click)="bulkRejectOpen = true"
              color="warn"
              class="reject-action"
            >
              <mat-icon aria-hidden="true">block</mat-icon>
              {{ t.getRequisitionRejectAllText() }}
            </button>
          </div>
          <p class="field-hint bulk-hint">
            {{ t.getRequisitionBulkDecisionHintText() }}
          </p>
          @if (bulkRejectOpen) {
            <div class="bulk-rejection">
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>{{
                  t.getRequisitionBulkRejectionReasonText()
                }}</mat-label>
                <textarea
                  matInput
                  required
                  maxlength="2048"
                  [(ngModel)]="bulkRejectionReason"
                  [disabled]="busy || reviewBlocked"
                ></textarea>
              </mat-form-field>
              @if (bulkRejectionReason && !canRejectAll()) {
                <p class="error">
                  {{ t.getRequisitionRejectionReasonValidationText() }}
                </p>
              }
              <div class="actions">
                <button
                  mat-flat-button
                  color="warn"
                  class="reject-action"
                  type="button"
                  [disabled]="busy || reviewBlocked || !canRejectAll()"
                  (click)="rejectAll()"
                >
                  <mat-icon aria-hidden="true">block</mat-icon>
                  {{ t.getRequisitionApplyRejectAllText() }}
                </button>
                <button
                  mat-button
                  type="button"
                  [disabled]="busy || reviewBlocked"
                  (click)="bulkRejectOpen = false; bulkRejectionReason = ''"
                >
                  <mat-icon aria-hidden="true">close</mat-icon>
                  {{ t.getCancelText() }}
                </button>
              </div>
            </div>
          }

          <div class="review-finalize">
            <div class="actions">
              <button
                mat-flat-button
                type="button"
                [disabled]="busy || bulkRejectOpen"
                (click)="finalizeReview()"
              >
                <mat-icon aria-hidden="true">fact_check</mat-icon>
                {{ t.getRequisitionFinalizeReviewText() }}
              </button>
            </div>
          </div>
        }
      </section>
    }
    @if (request.status === requisitionStatus.DRAFT) {
      <section class="card">
        <h2>{{ t.getRequisitionReviewText() }}</h2>
        @if (error) {
          <p role="alert" class="error action-feedback">{{ error }}</p>
        }
        @if (staleConflict) {
          <div class="conflict-actions">
            <a
              [routerLink]="['/requisitions', request.id]"
              target="_blank"
              rel="noopener"
            >
              {{ t.getRequisitionCompareLatestText() }}
            </a>
            <button
              mat-stroked-button
              type="button"
              [disabled]="busy"
              (click)="loadLatest()"
            >
              {{ t.getRequisitionLoadLatestText() }}
            </button>
          </div>
        }
        @if (unitConflict && !readonly) {
          <button
            mat-stroked-button
            type="button"
            [disabled]="busy || staleConflict"
            (click)="acceptCurrentUnit()"
          >
            {{
              t.getRequisitionAcceptUnitText(
                unitConflictProductName(),
                unitConflict.currentUnit
              )
            }}
          </button>
        }
        @if (notice && !hasUnsavedChanges()) {
          <p role="status" class="notice action-feedback">{{ notice }}</p>
        }
        @if (approversLoaded && !approvers.length) {
          <p class="submit-guidance">{{ t.getRequisitionNoApproverText() }}</p>
        }
        <mat-form-field
          appearance="outline"
          subscriptSizing="dynamic"
          class="approver-field"
          ><mat-label>{{ t.getRequisitionApproverText() }}</mat-label
          ><mat-select
            required
            [(ngModel)]="request.approverId"
            [disabled]="readonly || busy || !approvers.length"
          >
            @for (approver of approvers; track approver.id) {
              <mat-option [value]="approver.id">{{
                approver.email
              }}</mat-option>
            }
          </mat-select></mat-form-field
        >
        @if (approversLoaded && approvers.length && !canSubmit()) {
          <div class="submit-guidance">
            <p>{{ t.getRequisitionCompleteToSubmitText() }}</p>
            <ul>
              @if (!request.items.length) {
                <li>{{ t.getRequisitionEmptyItemsText() }}</li>
              }
              @if (hasInvalidQuantity()) {
                <li>{{ t.getRequisitionInvalidQuantityText() }}</li>
              }
              @if (!request.recipient.trim()) {
                <li>{{ t.getRequisitionRecipientText() }}</li>
              }
              @if (
                !request.deliveryAddress.trim() &&
                !request.collectionLocation.trim()
              ) {
                <li>{{ t.getRequisitionLocationText() }}</li>
              }
              @if (!hasValidApprover()) {
                <li>{{ t.getRequisitionApproverText() }}</li>
              }
            </ul>
          </div>
        }
        @if (!readonly) {
          <div class="actions">
            <button
              mat-stroked-button
              type="button"
              (click)="save()"
              [disabled]="busy || staleConflict"
            >
              <mat-icon aria-hidden="true">save</mat-icon>
              {{ t.getRequisitionSaveText() }}</button
            ><button
              mat-flat-button
              type="button"
              (click)="save(true)"
              [disabled]="busy || staleConflict || !canSubmit()"
            >
              <mat-icon aria-hidden="true">send</mat-icon>
              {{ t.getRequisitionSubmitText() }}
            </button>
          </div>
        }
      </section>
    }
  }
</main>
